'use client';

import { useTranslations } from 'next-intl';
import type { Table } from '@/lib/table-features';
import { Loader2, Trash2 } from 'lucide-react';
import { useHashDialog } from '@/hooks/use-hash-dialog';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { PermissionGate } from '@/features/auth';
import { usePermissions } from '@/hooks/use-permissions';
import { LABS_HASH } from '../constants';
import type { OfficeRow } from '../types';

interface LabsTableActionBarProps {
  table: Table<OfficeRow>;
  isPending: boolean;
  onBulkRemove: (indices: number[]) => void;
}

export function LabsTableActionBar({
  table,
  isPending,
  onBulkRemove
}: LabsTableActionBarProps) {
  const t = useTranslations('configurations');
  const tCommon = useTranslations('common');
  const { open: bulkOpen, onOpenChange: toggleBulk } = useHashDialog(
    LABS_HASH.bulkDelete
  );
  // `#bulk-delete` is deep-linkable, so the confirmation must check the
  // permission itself and not rely on its (hidden) trigger.
  const { hasPermission } = usePermissions();
  const canEditLabs = hasPermission('labs.edit');

  const selectedRows = table.getFilteredSelectedRowModel().rows;
  const count = selectedRows.length;

  function handleConfirm() {
    const indices = selectedRows.map((row) => row.original._idx);
    onBulkRemove(indices);
    table.resetRowSelection();
    toggleBulk(false);
  }

  return (
    <>
      <div className='bg-muted flex items-center gap-3 rounded-lg px-4 py-2'>
        <span className='text-muted-foreground text-sm font-medium'>
          {t('selectedCount', { count })}
        </span>
        <PermissionGate permissions={['labs.edit']}>
          <Button
            variant='destructive'
            size='sm'
            onClick={() => toggleBulk(true)}
            disabled={isPending}
          >
            <Trash2 className='me-1.5 h-3.5 w-3.5' />
            {t('bulkDelete')}
          </Button>
        </PermissionGate>
      </div>

      <AlertDialog open={canEditLabs && bulkOpen} onOpenChange={toggleBulk}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('bulkDeleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('bulkDeleteConfirm', { count })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tCommon('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              className={cn(
                'bg-destructive text-destructive-foreground hover:bg-destructive/90'
              )}
            >
              {isPending && <Loader2 className='me-2 h-4 w-4 animate-spin' />}
              {tCommon('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
